import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";

export const PRICE_REASON_REQUIRED = "price_change_reason_required";

/** True when a save failed because a live listing's price changed without a reason. */
export const isPriceReasonRequired = (err) => err?.response?.data?.code === PRICE_REASON_REQUIRED;

const NOTIFIED_HOW = ["Email", "Letter", "Phone + email", "In person + written"];

/**
 * Asks why a live listing's price changed and whether the vendor was told.
 * Kept on the property's price compliance history (Victorian underquoting
 * rules: the seller is told in writing when the estimate changes).
 */
export default function PriceChangeReasonModal({ open, onSubmit, onCancel }) {
  const [reason, setReason] = useState("");
  const [notified, setNotified] = useState(false);
  const [how, setHow] = useState(NOTIFIED_HOW[0]);

  useEffect(() => {
    if (open) { setReason(""); setNotified(false); setHow(NOTIFIED_HOW[0]); }
  }, [open]);

  const submit = () => {
    if (!reason.trim()) return;
    onSubmit?.({
      price_change_reason: reason.trim(),
      vendor_notified: notified,
      vendor_notified_how: notified ? how : "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Why is the price changing?</DialogTitle>
          <DialogDescription>
            This listing is live. The reason and the previous price are kept on the property's compliance file.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Reason</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Vendor revised the price after buyer feedback"
              aria-label="Reason for the price change"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={notified} onChange={(e) => setNotified(e.target.checked)} />
            The vendor has been notified in writing
          </label>
          {notified && (
            <div>
              <Label>How</Label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={how}
                onChange={(e) => setHow(e.target.value)}
                aria-label="How the vendor was notified"
              >
                {NOTIFIED_HOW.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          )}
          {!notified && (
            <p className="text-xs text-amber-700">
              You can record the vendor notification later from the Compliance tab.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={!reason.trim()}>Save price change</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * `const { requestReason, reasonModal } = usePriceChangeReason();`
 * `requestReason()` resolves with the extra fields to resend, or null if cancelled.
 * Render `{reasonModal}` once in the page.
 */
export function usePriceChangeReason() {
  const [open, setOpen] = useState(false);
  const resolver = useRef(null);

  const finish = useCallback((value) => {
    setOpen(false);
    const r = resolver.current;
    resolver.current = null;
    if (r) r(value);
  }, []);

  const requestReason = useCallback(() => new Promise((resolve) => {
    resolver.current = resolve;
    setOpen(true);
  }), []);

  const reasonModal = (
    <PriceChangeReasonModal open={open} onSubmit={(extra) => finish(extra)} onCancel={() => finish(null)} />
  );
  return { requestReason, reasonModal };
}

/** Append the reason fields to a FormData (AddPropertyPage sends FormData). */
export const appendPriceReason = (formData, extra) => {
  Object.entries(extra || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    formData.set ? formData.set(k, typeof v === "boolean" ? (v ? "true" : "false") : v)
      : formData.append(k, typeof v === "boolean" ? (v ? "true" : "false") : v);
  });
  return formData;
};
